import { getInstance } from "./index.js";
import store from "../store/index.js";

export const authGuard = (to, from, next) => {
  const authService = getInstance();

  const fn = async () => {
    // If the user is authenticated, continue with the route
    console.log("Inside auth guard. Is auth? ", authService.isAuthenticated);
    if (authService.isAuthenticated) {
      // meta.permission lets a route reuse an existing granted permission (e.g. "vue:read:programdetails")
      var check = to.meta?.permission || "vue:read:" + to.name.toLowerCase();
      if (
        store.state.permissions.includes(check) ||
        (store.state.magicLinkToken &&
          (check == "vue:read:wallet" ||
            check == "vue:read:paymentwizard" ||
            check == "vue:read:activity"))
      ) {
        // Audit + bulk claims admin: Client Admin only (not General / other admin roles)
        if (to.name === "admin-claims") {
          const roles = Array.isArray(store.state.roles) ? store.state.roles : [];
          if (!roles.includes("40ClientAdminV2")) {
            return next({ path: "/" });
          }
        }
        return next();
      } else {
        return next({ path: "/" });
      }
    }

    // Otherwise, log in
    console.log("We're logging in via redirect with auth guard", to.fullPath);
    if (to.fullPath.includes("login-error")) var url = "/";
    else url = to.fullPath;
    console.log("We're logging in via redirect with auth guard", url);
    authService.loginWithEmbedded({ appState: { targetUrl: url } });
  };

  // If loading has already finished, check our auth state using `fn()`
  if (!authService.loading) {
    return fn();
  }

  // Watch for the loading property to change before we check isAuthenticated
  authService.$watch("loading", (loading) => {
    if (loading === false) {
      console.log("Loading set to false inside authGuard");
      return fn();
    }
  });
};

/**
 * Route guard for the Programs list page (/programs), enforcing the client's
 * displayProgramPage flag. (#189743)
 *
 * The sidebar hides the Programs link when a client has the page disabled, but the route
 * itself was reachable by typing the URL — authGuard checks authentication and permission
 * only. Programs.vue carried a created() redirect, so the list page bounced after mounting.
 *
 * Use it on the list route only. The flag hides the list; it does not hide programs. The
 * program detail routes (/programs/:programId and /programs/:programId/dynamic) are how the
 * Program Dashboard, activity feed, leaderboard and program emails open a program, so they
 * stay on authGuard. Guarding them broke the dashboard for clients with the Programs page
 * off. (#193089)
 *
 * Runs authGuard first so authentication and permission behaviour is unchanged, and applies
 * the flag check only where authGuard would have allowed entry. The redirect target matches
 * what Programs.vue already used, so a user ends up in the same place however they arrive.
 *
 * Note: the same nav-hidden-but-route-open gap applies to the other page-display flags
 * (displayWalletPage, displayActivityPage, displayClaimPage, displayLibraryPage,
 * displayProgramDashboardPage). Those are not covered here — see #189743 for the audit.
 */
export const programPageGuard = (to, from, next) =>
  authGuard(to, from, (redirect) => {
    // authGuard rejected or redirected — pass its decision through untouched.
    if (redirect !== undefined) return next(redirect);

    // Fail CLOSED, matching Programs.vue's created() check exactly. An absent
    // userProfile.Client used to fall through to next() — so a cold load of
    // /programs could enter before the profile landed, making this guard weaker
    // than the component check it exists to replace. authGuard has already required
    // a populated permissions list by this point, so the remaining window (permissions
    // present, Client absent) is narrow, but "narrow" is not a reason for an
    // authorization check to default open.
    const client = store.state.userProfile?.Client;
    if (!client?.displayProgramPage) {
      return next(
        client?.displayProgramDashboardPage ? { name: "program-dashboard" } : { path: "/" }
      );
    }
    return next();
  });
